import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import {
    LocationNodeDeleteImpact,
    LocationNodeEdit,
    LocationNodeMapContext,
    LocationNodeMove,
    LocationNodeResponse,
    LocationNodeSummary,
    LocationNodeTree,
    LocationNodeType,
    LocationNodeTypeDefinition
} from '../models/location-node';

@Injectable({ providedIn: 'root' })
export class LocationNodeService {
    private readonly endpoint = `${environment.apiv2}/location-nodes`;

    constructor(private readonly http: HttpClient) {}

    getTree(rootId?: string, maxDepth = 32): Observable<LocationNodeTree[]> {
        let params = new HttpParams().set('maxDepth', maxDepth.toString());
        if (rootId) {
            params = params.set('rootId', rootId);
        }
        return this.http.get<LocationNodeTree[]>(`${this.endpoint}/tree`, { params });
    }

    search(search?: string, type?: LocationNodeType, withinNodeId?: string, take = 100): Observable<LocationNodeSummary[]> {
        let params = new HttpParams().set('take', take.toString());
        if (search) {
            params = params.set('search', search);
        }
        if (type !== undefined && type !== null) {
            params = params.set('type', type.toString());
        }
        if (withinNodeId) {
            params = params.set('withinNodeId', withinNodeId);
        }
        return this.http.get<LocationNodeSummary[]>(this.endpoint, { params });
    }

    get(id: string): Observable<LocationNodeResponse> {
        return this.http.get<LocationNodeResponse>(`${this.endpoint}/${id}`);
    }

    getEdit(id: string): Observable<LocationNodeEdit> {
        return this.http.get<LocationNodeEdit>(`${this.endpoint}/${id}/edit`);
    }

    getMapContext(parentId: string, excludeNodeId?: string): Observable<LocationNodeMapContext> {
        let params = new HttpParams();
        if (excludeNodeId) {
            params = params.set('excludeNodeId', excludeNodeId);
        }
        return this.http.get<LocationNodeMapContext>(`${this.endpoint}/${parentId}/map-context`, { params });
    }

    getTypes(): Observable<LocationNodeTypeDefinition[]> {
        return this.http.get<LocationNodeTypeDefinition[]>(`${this.endpoint}/types`);
    }

    create(model: LocationNodeEdit): Observable<LocationNodeResponse> {
        return this.http.post<LocationNodeResponse>(this.endpoint, model);
    }

    update(id: string, model: LocationNodeEdit): Observable<LocationNodeResponse> {
        return this.http.put<LocationNodeResponse>(`${this.endpoint}/${id}`, model);
    }

    move(id: string, model: LocationNodeMove): Observable<LocationNodeResponse> {
        return this.http.post<LocationNodeResponse>(`${this.endpoint}/${id}/move`, model);
    }

    getDeleteImpact(id: string): Observable<LocationNodeDeleteImpact> {
        return this.http.get<LocationNodeDeleteImpact>(`${this.endpoint}/${id}/delete-impact`);
    }

    archive(id: string, includeDescendants = false): Observable<void> {
        const params = new HttpParams().set('includeDescendants', includeDescendants.toString());
        return this.http.delete<void>(`${this.endpoint}/${id}`, { params });
    }
}
